import type { PublicPlan } from '@data/publicPlans';

function publicAssetUrl(relativePath: string): string {
	const base = import.meta.env.BASE_URL || '/';
	if (base === '/') {
		return `/${relativePath.replace(/^\/+/, '')}`;
	}
	const trimmed = base.replace(/\/+$/, '');
	return `${trimmed}/${relativePath.replace(/^\/+/, '')}`;
}

/** Same-origin placeholder so cards still show when Unsplash or uploads fail (e.g. Edge tracking/DNS). */
export const PLAN_CARD_FALLBACK_IMAGE = publicAssetUrl('questionnaire/emptygarden1.png');

/**
 * True when AI generation looks “real” (not only a stray after_image row from the API).
 * Used in plan detail UI so drafts do not present a stock “after” image as a finished design.
 */
export function aiGeneratedHasDesignContent(ag: PublicPlan['aiGenerated'] | undefined | null): boolean {
	if (!ag) return false;
	if ((ag.planDescription || '').trim().length >= 20) return true;
	if (Array.isArray(ag.keyFeatures) && ag.keyFeatures.some((k) => String(k).trim().length > 0)) return true;
	if (Array.isArray(ag.instructions) && ag.instructions.some((s) => String(s).trim().length > 0)) return true;
	const pb = ag.pricingBreakdown;
	if (pb && (Number(pb.max) > 0 || Number(pb.mid) > 0 || Number(pb.min) > 0)) return true;
	return false;
}

/**
 * Ordered URLs for plan cards (My Plans, Direct plans, etc.): try in order until one loads.
 * Prefer the active “after” design image whenever it exists (non-placeholder); use “before” only when there is no usable after.
 */
export function getPlanCardCoverCandidates(plan: PublicPlan): string[] {
	const afterRaw = (plan.aiGenerated?.photoAfterUrl || '').trim();
	const afterIsPlaceholder = !afterRaw || afterRaw.includes('unsplash.com');
	const hasRealAfter = !afterIsPlaceholder;
	const beforeRaw = (plan.photoBeforeUrl || plan.questionnaire?.photoBeforeUrl || '').trim();

	if (hasRealAfter) {
		return [afterRaw, beforeRaw, PLAN_CARD_FALLBACK_IMAGE].filter(Boolean);
	}
	return [beforeRaw, PLAN_CARD_FALLBACK_IMAGE].filter(Boolean);
}
